import { useState, useEffect } from 'react';
import { adminApi } from '../../utils/api';
import Button from './shared/Button';
import Badge from './shared/Badge';
import Input from './shared/Input';
import Select from './shared/Select';
import Spinner from './shared/Spinner';
import Alert from './shared/Alert';
import Modal from './shared/Modal';
import Pagination from './shared/Pagination';

const SCORE_VARIANTS = {
    1: 'secondary', 2: 'info', 3: 'warning', 4: 'danger', 5: 'danger',
};

function ScoreBadge({ score }) {
    return <Badge variant={SCORE_VARIANTS[score] || 'secondary'}>{score}</Badge>;
}

const STATUS_VARIANTS = {
    new: 'info', processing: 'warning', completed: 'success', failed: 'danger',
};

function StatusBadge({ status }) {
    return <Badge variant={STATUS_VARIANTS[status] || 'secondary'}>{status}</Badge>;
}

export default function AdminSignalOutputs() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [stats, setStats] = useState(null);
    const [detail, setDetail] = useState(null);

    const [filters, setFilters] = useState({
        ticker: '',
        event_type: '',
        event_date: '',
        source: '',
        signal_status: '',
        limit: 50,
        offset: 0,
    });

    useEffect(() => { loadStats(); }, []);
    useEffect(() => { loadData(); }, [filters]);
    useEffect(() => {
        if (successMessage) {
            const t = setTimeout(() => setSuccessMessage(null), 5000);
            return () => clearTimeout(t);
        }
    }, [successMessage]);

    const loadStats = async () => {
        try {
            const data = await adminApi.getSignalOutputStats();
            setStats(data);
        } catch { /* ignore */ }
    };

    const loadData = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await adminApi.listSignalOutputs(filters);
            setItems(data.items || []);
            setTotal(data.total || 0);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleFilter = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value, offset: 0 }));
    };

    const handleDelete = async (e, outputId) => {
        e.stopPropagation();
        if (!confirm(`Delete signal output #${outputId}?`)) return;
        try {
            await adminApi.deleteSignalOutput(outputId);
            setSuccessMessage(`Output #${outputId} deleted`);
            loadData();
            loadStats();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleViewDetail = async (outputId) => {
        try {
            const data = await adminApi.getSignalOutput(outputId);
            setDetail(data);
        } catch (err) {
            setError(err.message);
        }
    };

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <h1 className="text-lg font-semibold text-gray-900">Signal Outputs</h1>
                {stats && (
                    <div className="flex gap-4 text-sm text-gray-500">
                        <span>Total: <strong className="text-gray-900">{stats.total}</strong></span>
                        {stats.latest_at && (
                            <span>Latest: <strong className="text-gray-900">{new Date(stats.latest_at).toLocaleString()}</strong></span>
                        )}
                    </div>
                )}
            </div>

            {stats && stats.by_event_type.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                    {stats.by_event_type.map(({ event_type, count }) => (
                        <button
                            key={event_type}
                            onClick={() => handleFilter('event_type', filters.event_type === event_type ? '' : event_type)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                                filters.event_type === event_type
                                    ? 'bg-blue-50 border-blue-300 text-blue-700'
                                    : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                            }`}
                        >
                            {event_type} ({count})
                        </button>
                    ))}
                </div>
            )}

            {error && <Alert variant="error" className="mb-4" onClose={() => setError(null)}>{error}</Alert>}
            {successMessage && <Alert variant="success" className="mb-4">{successMessage}</Alert>}

            <div className="flex flex-wrap gap-2 mb-4">
                <Input
                    placeholder="Ticker"
                    value={filters.ticker}
                    onChange={(e) => handleFilter('ticker', e.target.value)}
                    className="w-32"
                />
                <Input
                    placeholder="Source (task_id)"
                    value={filters.source}
                    onChange={(e) => handleFilter('source', e.target.value)}
                    className="w-48"
                />
                <Input
                    type="date"
                    value={filters.event_date}
                    onChange={(e) => handleFilter('event_date', e.target.value)}
                    className="w-40"
                />
                <Select
                    value={filters.signal_status}
                    onChange={(e) => handleFilter('signal_status', e.target.value)}
                    options={[
                        { value: '', label: 'All Status' },
                        { value: 'new', label: 'New' },
                        { value: 'processing', label: 'Processing' },
                        { value: 'completed', label: 'Completed' },
                        { value: 'failed', label: 'Failed' },
                    ]}
                    className="w-36"
                />
                {(filters.ticker || filters.event_type || filters.event_date || filters.source || filters.signal_status) && (
                    <Button
                        variant="ghost" size="sm"
                        onClick={() => setFilters(prev => ({ ...prev, ticker: '', event_type: '', event_date: '', source: '', signal_status: '', offset: 0 }))}
                    >
                        Clear
                    </Button>
                )}
            </div>

            {loading ? (
                <Spinner size="lg" text="Loading..." center />
            ) : items.length === 0 ? (
                <div className="text-center py-12 text-gray-500">No signal outputs found.</div>
            ) : (
                <>
                    <div className="border border-gray-200 rounded-lg overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-200 bg-gray-50/60">
                                    <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">ID</th>
                                    <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Event</th>
                                    <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Ticker</th>
                                    <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Date</th>
                                    <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Type</th>
                                    <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Score</th>
                                    <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Status</th>
                                    <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Source</th>
                                    <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Created</th>
                                    <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {items.map(item => (
                                    <tr
                                        key={item.output_id}
                                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                                        onClick={() => handleViewDetail(item.output_id)}
                                    >
                                        <td className="px-4 py-3 text-xs text-gray-400 font-mono">#{item.output_id}</td>
                                        <td className="px-4 py-3">
                                            <div className="text-xs font-mono text-gray-500">{item.event_id}</div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <Badge variant="info">{item.ticker}</Badge>
                                        </td>
                                        <td className="px-4 py-3 text-xs text-gray-600">{item.event_date}</td>
                                        <td className="px-4 py-3">
                                            <Badge variant="secondary">{item.event_type}</Badge>
                                        </td>
                                        <td className="px-4 py-3"><ScoreBadge score={item.signal_score} /></td>
                                        <td className="px-4 py-3"><StatusBadge status={item.status} /></td>
                                        <td className="px-4 py-3 text-xs text-gray-500 font-mono">{item.source || '-'}</td>
                                        <td className="px-4 py-3 text-xs text-gray-500">
                                            {new Date(item.created_at).toLocaleString()}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <Button
                                                variant="ghost" size="xs" className="text-red-600"
                                                onClick={(e) => handleDelete(e, item.output_id)}
                                            >
                                                Delete
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-4">
                        <Pagination
                            total={total}
                            offset={filters.offset}
                            limit={filters.limit}
                            onChange={(newOffset) => setFilters(prev => ({ ...prev, offset: newOffset }))}
                        />
                    </div>
                </>
            )}

            <Modal
                open={!!detail}
                title={detail ? `Signal Output #${detail.output_id}` : ''}
                onClose={() => setDetail(null)}
                size="xl"
            >
                {detail && (
                    <div className="p-5 space-y-5">
                        <div className="grid grid-cols-3 gap-4 text-sm">
                            <InfoItem label="Event ID" value={detail.event_id} mono />
                            <InfoItem label="Event Type" value={detail.event_type} />
                            <InfoItem label="Ticker" value={detail.ticker} />
                            <InfoItem label="Event Date" value={detail.event_date} />
                            <InfoItem label="Transaction Type" value={detail.transaction_type} />
                            <InfoItem label="Signal Score" value={detail.signal_score} />
                            <InfoItem label="Source" value={detail.source || '-'} mono />
                            <div>
                                <span className="text-gray-500 text-xs">Status</span>
                                <div className="mt-1"><StatusBadge status={detail.status} /></div>
                            </div>
                            <InfoItem label="Created" value={new Date(detail.created_at).toLocaleString()} />
                        </div>

                        <JsonSection title="Layer 1 Result" data={detail.layer1_result} />
                        <JsonSection title="Layer 2 Result" data={detail.layer2_result} />
                        <JsonSection title="Layer 3 Result" data={detail.layer3_result} />
                        <JsonSection title="Retail Output" data={detail.retail_output} />
                        <JsonSection title="Advisor Output" data={detail.advisor_output} />
                        <JsonSection title="Signal Factors" data={detail.signal_factors} />
                    </div>
                )}
            </Modal>
        </div>
    );
}

function InfoItem({ label, value, mono = false }) {
    return (
        <div>
            <span className="text-gray-500 text-xs">{label}</span>
            <div className={`mt-0.5 font-medium ${mono ? 'font-mono text-xs' : ''}`}>{value ?? '-'}</div>
        </div>
    );
}

function JsonSection({ title, data }) {
    if (!data) return null;
    return (
        <div>
            <span className="text-sm font-medium text-gray-700">{title}</span>
            <pre className="mt-1 p-3 bg-gray-50 border border-gray-200 rounded-lg text-xs whitespace-pre-wrap break-words max-h-64 overflow-y-auto">
                {JSON.stringify(data, null, 2)}
            </pre>
        </div>
    );
}
